import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ProductForm,
  type ProductFormData,
} from "../../components/products/ProductForm";
import { showToast } from "../../lib/toast";
import { productService } from "../../services/product.service";

export function EditProductPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [error, setError] = useState("");
  const [productData, setProductData] =
    useState<Partial<ProductFormData> | null>(null);

  useEffect(() => {
    if (id) {
      loadProduct(id);
    }
  }, [id]);

  const loadProduct = async (productId: string) => {
    try {
      setLoadingProduct(true);
      const product = await productService.getProduct(productId);
      setProductData({
        name: product.name,
        sku: product.sku,
        barcode: product.barcode || "",
        category: product.category || "Allopathy",
        subcategory: product.subcategory || "",
        description: product.description || "",
        composition: product.composition || "",
        saltComposition: product.saltComposition || "",
        sideEffects: product.sideEffects || "",
        manufacturer: product.manufacturer || "",
        purchasePrice: Number(product.purchasePrice),
        sellingPrice: Number(product.sellingPrice),
        mrp: Number(product.mrp),
        gstPercent: Number(product.gstPercent),
        isGstExempted: !!product.isGstExempted,
        scheduleType: product.scheduleType as any,
        unit: product.unit,
        strength: product.strength || "",
        minStockLevel: product.minStockLevel || undefined,
        maxStockLevel: product.maxStockLevel || undefined,
        hsnCode: product.hsnCode || "",
        status: product.status as any,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load product");
    } finally {
      setLoadingProduct(false);
    }
  };

  const onSubmit = async (data: ProductFormData) => {
    if (!id) return;
    setError("");
    setLoading(true);

    try {
      await productService.updateProduct(id, data);
      showToast.success("Product updated successfully");
      navigate("/products");
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.message || "Failed to update product";
      showToast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingProduct) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-gray-500">Loading product...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Product</h1>
          <p className="text-sm text-gray-600 mt-1">
            Update product information
          </p>
        </div>
        <button
          onClick={() => navigate("/products")}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          &larr; Back
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {productData && (
        <ProductForm
          mode="edit"
          defaultValues={productData}
          onSubmit={onSubmit}
          onCancel={() => navigate("/products")}
          isLoading={loading}
        />
      )}
    </div>
  );
}
